import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Zap, Flame, Trophy } from 'lucide-react';

const SESSION_KEY = 'exit_intent_shown';

const ExitIntentPopup = () => {
  const [visible, setVisible] = useState(false);
  const navigate = useNavigate();

  const show = useCallback(() => {
    if (sessionStorage.getItem(SESSION_KEY)) return;
    sessionStorage.setItem(SESSION_KEY, '1');
    setVisible(true);
  }, []);

  useEffect(() => {
    let armed = false;
    const armTimer = setTimeout(() => { armed = true; }, 2000);

    // Desktop: mouse leaves toward top
    const onMouseLeave = (e) => {
      if (armed && e.clientY <= 5) show();
    };
    document.addEventListener('mouseleave', onMouseLeave);

    // Mobile: 30s inactivity
    let inactivityTimer = null;
    const resetInactivity = () => {
      clearTimeout(inactivityTimer);
      inactivityTimer = setTimeout(() => { if (armed) show(); }, 30000);
    };
    const isMobile = 'ontouchstart' in window;
    if (isMobile) {
      resetInactivity();
      ['touchstart', 'scroll'].forEach(evt =>
        window.addEventListener(evt, resetInactivity, { passive: true })
      );
    }

    return () => {
      clearTimeout(armTimer);
      clearTimeout(inactivityTimer);
      document.removeEventListener('mouseleave', onMouseLeave);
      if (isMobile) {
        ['touchstart', 'scroll'].forEach(evt =>
          window.removeEventListener(evt, resetInactivity)
        );
      }
    };
  }, [show]);

  const close = () => setVisible(false);
  const goQuiz = () => { close(); navigate('/quiz'); };

  return (
    <AnimatePresence>
      {visible && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.25 }}
          className="fixed inset-0 z-[9999] flex items-center justify-center p-4"
          data-testid="exit-intent-overlay"
          onClick={close}
        >
          {/* Backdrop */}
          <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" />

          {/* Card */}
          <motion.div
            initial={{ opacity: 0, scale: 0.9, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.95, y: 10 }}
            transition={{ duration: 0.3, ease: 'easeOut' }}
            onClick={(e) => e.stopPropagation()}
            className="relative w-full max-w-md backdrop-blur-2xl bg-slate-900/90 border border-cyan-500/20 rounded-2xl p-8 sm:p-10 shadow-[0_0_60px_rgba(6,182,212,0.15)]"
            data-testid="exit-intent-popup"
          >
            {/* Close */}
            <button
              onClick={close}
              className="absolute top-4 right-4 text-slate-500 hover:text-white transition-colors"
              data-testid="exit-intent-close"
            >
              <X className="h-5 w-5" />
            </button>

            {/* Glow accents */}
            <div className="absolute -top-16 -right-16 w-40 h-40 bg-cyan-500/10 rounded-full blur-3xl pointer-events-none" />
            <div className="absolute -bottom-12 -left-12 w-32 h-32 bg-emerald-500/10 rounded-full blur-3xl pointer-events-none" />

            <div className="relative">
              <h2 className="font-heading text-2xl sm:text-3xl font-bold text-white mb-3 leading-tight">
                Wait — Before You Go!
              </h2>
              <p className="text-slate-400 text-base mb-6 leading-relaxed">
                Find out how much weight you could realistically lose in 90 days. Takes 2 minutes. Completely free.
              </p>

              {/* Social proof */}
              <div className="space-y-3 mb-8">
                <div className="flex items-start gap-3">
                  <Zap className="h-5 w-5 text-yellow-400 mt-0.5 flex-shrink-0" />
                  <span className="text-slate-300 text-sm">Deby D. lost <strong className="text-white">11 lbs in just 15 days</strong></span>
                </div>
                <div className="flex items-start gap-3">
                  <Flame className="h-5 w-5 text-orange-400 mt-0.5 flex-shrink-0" />
                  <span className="text-slate-300 text-sm">Kim R. is down <strong className="text-white">69 lbs and counting</strong></span>
                </div>
                <div className="flex items-start gap-3">
                  <Trophy className="h-5 w-5 text-amber-400 mt-0.5 flex-shrink-0" />
                  <span className="text-slate-300 text-sm">Deborah lost <strong className="text-white">50 lbs</strong> — and came back for round 2</span>
                </div>
              </div>

              {/* CTA */}
              <button
                onClick={goQuiz}
                className="w-full py-4 rounded-full bg-gradient-to-r from-emerald-500 to-green-500 hover:from-emerald-400 hover:to-green-400 text-white font-bold text-base shadow-[0_0_30px_rgba(16,185,129,0.4)] hover:shadow-[0_0_40px_rgba(16,185,129,0.6)] transition-all"
                data-testid="exit-intent-cta"
              >
                Get My Free AI Forecast →
              </button>

              {/* Dismiss */}
              <button
                onClick={close}
                className="w-full mt-3 py-2 text-slate-500 hover:text-slate-400 text-sm transition-colors"
                data-testid="exit-intent-dismiss"
              >
                No thanks, I'll figure it out on my own
              </button>

              {/* Disclaimer */}
              <p className="text-center text-xs text-slate-600 mt-4">
                Free · 2 minutes · No pressure · Results vary
              </p>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default ExitIntentPopup;
